import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Table } from "react-bootstrap";
import "./License.css";
import "../../Dashboard/Dashboard.css";
import "../../../../../common/styles/table.css";
import TablePagination from "../../../TeacherAdmin/pagination/TablePagination";
import { useLicenseOverview } from "../../../../../hooks/organization/useLicenseOverview";
import DashboardStats from "../../../../common/Dashboard/DashboardStats";
import DashboardStatsSkeleton from "../../../../common/Dashboard/DashboardStatsSkeleton";
import TableSkeleton from "../../../common/TableSkeleton";

/**
 * @typedef {Object} LicenseActivity
 * @property {string} date
 * @property {string} action
 * @property {number} count
 * @property {string|null} performed_by
 * @property {string|null} remarks
 */

/**
 * @typedef {"school" | "organization"} TenantType
 */

/**
 * Licenses component - Main licenses dashboard
 * Displays license statistics and activity table
 *  @param {{ type?: TenantType }} props
 * @returns {JSX.Element}
 */

export const Licenses = ({ type = "school" }) => {
  const [currentPage, setCurrentPage] = useState(1);

  // Use the license overview hook
  const { licenseData, loading, error, getLicenseOverview } =
    useLicenseOverview();

  // Fetch license data on component mount
  useEffect(() => {
    getLicenseOverview();
  }, []);

  // Pagination settings
  const totalPages = 1; // Update this when integrating with paginated API

  /**
   * Format date string to readable format
   * @param {string} dateString
   * @returns {string}
   */
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const options = { day: "numeric", month: "short", year: "numeric" };
    return date.toLocaleDateString("en-IN", options);
  };



  // Loading state
  if (loading) {
    return (
      <div className="">
        <div className="">
          {/* Header */}
          <div className="license-header">
            <h1 className="license-title">Licenses</h1>
          </div>

          {/* Loading skeleton for stats */}
          <DashboardStatsSkeleton count={3} />

          {/* Loading skeleton for table */}
          <TableSkeleton rows={5} columns={5} />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="">
        <div className="">
          {/* Header */}
          <div className="license-header">
            <h1 className="license-title">Licenses</h1>
          </div>

          {/* Error Alert */}
          <div className="alert alert-danger mt-3" role="alert">
            <div className="d-flex align-items-center justify-content-between">
              <span>
                <i className="fa-solid fa-circle-exclamation me-2"></i>
                {error}
              </span>
              <button
                className="btn btn-outline-danger btn-sm"
                onClick={getLicenseOverview}
              >
                <i className="fa-solid fa-rotate-right me-1"></i>
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Get stats from API data
  const licenseStats = {
    total: licenseData?.stats?.total_licenses ?? 0,
    assigned: licenseData?.stats?.assigned_licenses ?? 0,
    available: licenseData?.stats?.available_licenses ?? 0,
  };

  // Get activities from API data
  const activities = licenseData?.activity || [];

  // Prepare stats for DashboardStats component
  const licenseStatsData = [
    {
      label: "Total Licenses",
      value: licenseStats.total.toLocaleString(),
      icon: "fa-id-card",
      colorClass: "dashboard-stat-card-green",
    },
    {
      label: "Assigned Licenses",
      value: licenseStats.assigned.toLocaleString(),
      icon: "fa-user-check",
      colorClass: "dashboard-stat-card-purple",
    },
    {
      label: "Available Licenses",
      value: licenseStats.available.toLocaleString(),
      icon: "fa-users",
      colorClass: "dashboard-stat-card-peach",
    },
  ];

  return (
    <div className="">
      <div className="">
        {/* Stats Cards using DashboardStats */}
        <DashboardStats stats={licenseStatsData} />

        {/* Activity Table Header */}
        <div className="license-activity-header">
          <h2 className="license-activity-title">License Activity Table</h2>
          <Link
            to={
              type === "school"
                ? "/school-admin/settings/purchase-licenses?license-type=add-on"
                : "/organization/settings/purchase-licenses?license-type=add-on"
            }
            className="purchase-license-btn"
          >
            + Purchase Licenses
          </Link>
        </div>

        {/* Activity Table */}
        <div className="custom-table-container mt-3">
          <div className="custom-table-wrapper">
            <Table hover className="custom-table">
              <thead className="custom-table-head">
                <tr>
                  <th>Date</th>
                  <th>Action</th>
                  <th>Licenses Count</th>
                  <th>Performed By</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {activities.length > 0 ? (
                  activities.map((activity, index) => (
                    <tr key={index}>
                      <td>{formatDate(activity.date)}</td>
                      <td>{activity.action || "-"}</td>
                      <td>{activity.count?.toLocaleString() || "-"}</td>
                      <td>{activity.performed_by || "-"}</td>
                      <td>{activity.remarks || "-"}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5}>
                      <div className="text-center m-5">
                        No activities found.
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <TablePagination
            totalPage={totalPages}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            disabled={false}
          />
        </div>
      </div>
    </div>
  );
};

export default Licenses;
